import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import moment from 'moment';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import TWIADateFormatUtil from "ent-capability-quoteandbind-common-react/util/TWIADateFormatUtil";
import { GatewayEndorsementService as LoadSaveService} from 'twia-capability-gateway-policychange';
import _, { update } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { TranslatorContext } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
import ErrorPopupUtil from 'ent-components-platform-react/util/ErrorPopupUtil';
import TWIAWizardHelper from 'producer-engage/src/overrides/modules-react/twia-portals-wizard-components-ui/Wizard/components/util/TWIAWizardHelper';
import metadata from './EffectiveDate.metadata.json5';
import messages from './EffectiveDate.messages'
import styles from './EffectiveDate.module.scss'

function TWIAEffectiveDatePage(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { wizardData: submissionVM, updateWizardData } = props;
    const { authHeader } = useAuthentication();
    const { isComponentValid, onValidate, initialValidation} = useValidation('TWIAEffectiveDatePage'); 
    const { refreshData } = useDataRefresh();
    const translator = useContext(TranslatorContext);
    const [showErrors, setShowErrors] = useState(false);
    const { showAlert } = useModal();
    const [showMinDateMessage, setShowMinDateMessage] = useState(false);
    const [showScheduledMinDateMessage, setShowScheduledMinDateMessage] = useState(false);
    const [showMaxDateMessage, setShowMaxDateMessage] = useState(false);
    const minimumEffectiveDate = _.get(submissionVM, `baseData.minimumEffectiveDate.value`);
    const coverageEndDate = _.get(submissionVM, `baseData.coverageEnd_TWIA.value`);
    const formattedMinEffectiveDate = TWIADateFormatUtil.formatDate(minimumEffectiveDate);
    const formattedCoverageEndDate = TWIADateFormatUtil.formatDate(coverageEndDate);
    const intEffectiveDate = _.get(submissionVM, `baseData.initialEffectiveDate.value`);
    const today = LocalDateUtil.today();
    const isMinDateValid = moment(intEffectiveDate).isSameOrAfter(today);

    const onNext = useCallback(async() => {
        if (!isComponentValid) {
            setShowErrors(true);

            return false
        }

        try {
            _.set(submissionVM, 'baseData.policyAddress.mustStandardize_Ext', false)
            _.set(submissionVM, 'lobData.commercialProperty.coverages', undefined) // DE17700
            _.set(submissionVM, 'lobData.commercialProperty.coverables', undefined)

            const updatedSubmission = await LoadSaveService.saveEndorsement(
                submissionVM.value,
                authHeader
            );

            submissionVM.value = updatedSubmission;
            updateWizardData(submissionVM);

            return submissionVM;

        } catch (err) {
            ErrorPopupUtil.showSaveErrorPopup(showAlert, true);

            return false;
        }

    }, [isComponentValid, submissionVM, authHeader, updateWizardData, showAlert]);


    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);

            // DE17433
            const todayDate = LocalDateUtil.today();
            const minEffectiveDate = _.get(submissionVM, `baseData.minimumEffectiveDate.value`);
            const isMinimumDateValid = moment(value).isSameOrAfter(todayDate);
            const isScheduledMinimumDateValid = moment(value).isSameOrAfter(minEffectiveDate);
            const isMaximumDateValid = moment(value).isSameOrBefore(coverageEndDate);


            setShowMaxDateMessage(!isMaximumDateValid);

            // DE17707
            if (moment(minEffectiveDate).isAfter(todayDate)) {
                setShowScheduledMinDateMessage(!isScheduledMinimumDateValid);
            } else if (moment(minEffectiveDate).isSame(todayDate)) {
                setShowMinDateMessage(!isMinimumDateValid);
            }

            _.set(submissionVM, 'baseData.effectiveDate', value);
            setShowErrors(true);
            updateWizardData(submissionVM);
            refreshData();
        }, [coverageEndDate, refreshData, submissionVM, updateWizardData]
    );

    const overrideProps = {
        '@field': {
            // apply to all fields
            showOptional: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        minDateMsg: {
            value: `Minimum date is ${formattedMinEffectiveDate}`,
            visible: showMinDateMessage,
        },
        minScheduledDateMsg: {
            value: `Minimum date is ${formattedMinEffectiveDate}`, // DE17707
            visible: showScheduledMinDateMessage,
        },
        maxDateMsg: {
            value: `Maximum date is ${formattedCoverageEndDate}`,
            visible: showMaxDateMessage,
        }

    };

    const skip = useCallback(async () => {
        if (!isMinDateValid) {
            return false; // DE17904
        }

        return TWIAWizardHelper.skipStep(submissionVM, initialValidation);
    }, [isMinDateValid, initialValidation, submissionVM]);    

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <WizardPage
            onNext={onNext}
            showPrevious = {false}
            skipWhen = {skip}
            disableNext = {!isMinDateValid || !submissionVM.baseData.initialEffectiveDate.aspects.valid}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                showErrors={showErrors}
                onValueChange={writeValue}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
            />
        </WizardPage>
    );
}

TWIAEffectiveDatePage.propTypes = wizardProps;
export default TWIAEffectiveDatePage;